import React, { useCallback, useMemo } from 'react';
import difference from 'lodash/difference';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import { HeaderProps } from '@/components/Modals/types';
import useDebounceState from '@/hooks/useDebounceState';
import useEntitySet from '@/hooks/useEntitySet';
import ListItem from '@/modules/corporate/modules/group/components/GroupListItem';
import useGroups from '@/modules/corporate/modules/group/views/GroupManager/hooks/useGroups';
import { LearnerProfileActionTypes } from '@/modules/learner/views/LearnerProfile/hooks/useLearnerProfile/actions';
import { GroupStatusEnum, GroupTypeEnum, UserCollectionGroupType } from '@/types/schema';

import { LearnerGroupsWidgetProps } from '../LearnerGroupsWidget/types';

type Props = LearnerGroupsWidgetProps & {
  onClose: () => void;
  header?: Partial<Pick<HeaderProps, 'title' | 'description'>>;
};

const LearnerEditGroupsModal = (props: Props) => {
  const { t } = useTranslation('Group');
  const { header, learnerId, userGroups, onAction, onClose } = props;

  const [searchText, setSearchText, debounceSearchText] = useDebounceState('');

  const { autoSyncGroupIdsSet, learnerGroupIds } = useMemo(() => {
    const autoSyncGroupIdsSet = new Set<string>();

    return {
      autoSyncGroupIdsSet,
      learnerGroupIds: userGroups.map((group) => {
        if (group.isAutoSync) {
          autoSyncGroupIdsSet.add(group.id);
        }

        return group.id;
      }),
    };
  }, [userGroups]);

  const { selectedItemsSet, toggle } = useEntitySet(learnerGroupIds);
  const { groups, isFetching, fetchMore } = useGroups({
    filters: {
      name: debounceSearchText,
      systemGenerated: false,
      status: [GroupStatusEnum.Published],
      types: [GroupTypeEnum.UserCollection],
    },
  });

  const onSubmit = useCallback(async () => {
    const selectedGroupIds = Array.from(selectedItemsSet);
    const removedGroupIds = difference(learnerGroupIds, selectedGroupIds);
    const addedGroupIds = difference(selectedGroupIds, learnerGroupIds);

    if (addedGroupIds.length || removedGroupIds.length) {
      await onAction({
        type: LearnerProfileActionTypes.UpdateProfile,
        payload: {
          updatedValues: {
            userId: learnerId,
            groupIds: {
              add: addedGroupIds,
              remove: removedGroupIds,
            },
          },
        },
      });
      onClose();
    } else {
      onClose();
    }
  }, [selectedItemsSet, learnerGroupIds, onAction, learnerId, onClose]);

  const renderContent = useCallback(() => {
    if (isFetching) {
      return <Loader />;
    }

    if (!groups.length) {
      if (searchText) {
        return (
          <EmptyState
            className="flex-1"
            description={t('Group:Modal.SelectGroupsEmptyDescription', { text: searchText })}
            title={t('Group:Modal.SelectGroupsEmptyTitle')}
          />
        );
      }

      return (
        <EmptyState
          className="flex-1"
          description={t('Group:Modal.NoGroupsDescription')}
          title={t('Group:Modal.NoGroupsAvailable')}
        />
      );
    }

    if (groups.length) {
      return (
        <ContentListModal.Body
          className="flex-1"
          itemRenderer={(index: number) => {
            const group = groups[index] as UserCollectionGroupType;

            return (
              <ListItem
                key={group.id}
                disabled={autoSyncGroupIdsSet.has(group.id)}
                group={group}
                selected={selectedItemsSet.has(group.id)}
                onSelect={toggle}
              />
            );
          }}
        />
      );
    }

    return null;
  }, [autoSyncGroupIdsSet, groups, isFetching, searchText, selectedItemsSet, t, toggle]);

  return (
    <ContentListModal.Main
      action={{
        discardText: t('Common:Cancel'),
        text: t('Common:Actions.Save'),
        onSubmit,
      }}
      fetchMore={fetchMore}
      header={{
        title: t('LearnerProfile:Groups.EditGroups'),
        description: t('LearnerProfile:Groups.EditGroupsDescription'),
        ...header,
      }}
      items={groups}
      searchBar={{
        value: searchText,
        onChange: setSearchText,
        selectedCount: selectedItemsSet.size,
        placeholder: t('Common:Search'),
      }}
      onClose={onClose}>
      {renderContent()}
    </ContentListModal.Main>
  );
};

export default LearnerEditGroupsModal;
